/** Text shown beside a placed symbol: its tag and its property summary. Shared by canvas and exports. */
import { elementBounds, type Placement } from './geometry.ts';
import type { SymbolDef } from './types.ts';

export interface LabelLine {
  text: string;
  x: number;
  y: number;
  size: number;
  anchor: 'start' | 'middle';
  bold: boolean;
}

type Props = Record<string, string | number | boolean>;

export const TAG_SIZE = 2.5;
export const FIELD_SIZE = 2.2;

export function fillTemplate(template: string, props: Props, def: SymbolDef): string | null {
  let missing = false;
  const s = template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const field = def.fields?.find((f) => f.key === key);
    const v = props[key] ?? (field && 'default' in field ? field.default : undefined);
    const hidden = field?.type === 'select' && field.omitDefault && v === field.default;
    if (v === undefined || v === '' || v === null || v === false || hidden) {
      missing = true;
      return '';
    }
    return String(v).replace('.', ',');
  });
  return missing ? null : s.trim();
}

/** Property summary lines for a symbol, e.g. ["C16 A", "30 mA type A"]. */
export function summaryLines(def: SymbolDef, props: Props): string[] {
  return (def.label ?? []).map((t) => fillTemplate(t, props, def)).filter((s): s is string => !!s);
}

/**
 * Label layout: to the right of the symbol, or above an in-line device turned sideways.
 */
export function elementLabels(
  def: SymbolDef,
  el: Placement & { tag: string; props: Props },
  extra: string[] = [],
): LabelLine[] {
  if (def.freeText || def.room) return [];
  const tagText =
    el.tag && def.tagFormat
      ? def.tagFormat
          .replace(/\{(\w+)\}/g, (_, k: string) => (k === 'tag' ? el.tag : String(el.props[k] ?? '')))
          .replace(/:$/, '')
      : el.tag;
  const lines = [
    ...(tagText ? [{ text: tagText, bold: true }] : []),
    ...summaryLines(def, el.props).map((text) => ({ text, bold: false })),
    ...extra.map((text) => ({ text, bold: false })),
  ];
  if (!lines.length) return [];
  const b = elementBounds(def, el);
  const lineH = 3;
  // Terminals sit 10 mm apart in strips: one compact label just right of the conductor,
  // below the terminal, so neighbours never overlap.
  if (def.id === 'iec.terminal') {
    const vertical = el.rotation === 0 || el.rotation === 180;
    return lines.slice(0, 1).map((l) => ({
      ...l,
      bold: false,
      x: vertical ? (b.minX + b.maxX) / 2 + 1.2 : b.maxX + 1,
      y: vertical ? b.maxY + 2.4 : (b.minY + b.maxY) / 2 + 0.7,
      size: 2,
      anchor: 'start' as const,
    }));
  }
  // Labels go to the right, except for in-line devices turned sideways (a breaker on a
  // horizontal run): there the wire continues to the right, so labels go above.
  const sideways = el.rotation === 90 || el.rotation === 270;
  const inLine = def.ports.length >= 2;
  if (!sideways || !inLine) {
    const x = b.maxX + 1.5;
    const y0 = (b.minY + b.maxY) / 2 - ((lines.length - 1) * lineH) / 2 + 0.9;
    return lines.map((l, i) => ({
      ...l,
      x,
      y: y0 + i * lineH,
      size: l.bold ? TAG_SIZE : FIELD_SIZE,
      anchor: 'start',
    }));
  }
  const x = (b.minX + b.maxX) / 2;
  const yEnd = b.minY - 1.5;
  return lines.map((l, i) => ({
    ...l,
    x,
    y: yEnd - (lines.length - 1 - i) * lineH,
    size: l.bold ? TAG_SIZE : FIELD_SIZE,
    anchor: 'middle',
  }));
}
